import { useCallback, useMemo, useState } from 'react';
import { ThemeConfig } from '@visx/xychart/lib/theme/buildChartTheme';

import { ScaleBounds } from '../charts.model';

import { createTheme } from './create-theme';
import { formatPercent } from './formatters';

export const calculateBounds = (data: any[], keys: any[]) => {
    if (data.length === 0 || keys.length === 0) {
        return { sum: 0, max: 0, min: 0 };
    }

    return data.reduce<ScaleBounds>((allDaily, currentDate) => {
        const values = keys.map(key => currentDate[key]);
        const dSum = values.reduce((daily, value) => daily + value, 0);
        const dMin = Math.min(...values);
        const dMax = Math.max(...values);

        if (!allDaily) {
            return { sum: dSum, max: dMax, min: dMin };
        }
        return {
            sum: allDaily.sum + dSum,
            max: Math.max(allDaily.max, dMax),
            min: Math.min(allDaily.min, dMin),
        };
    }, undefined!);
};

export const useChartProps = <T extends {}>({
    dateKey,
    keys,
    data,
    stack = false,
    threshold,
    boundKeys,
    theme: themeOverrides,
}: {
    data: T[];
    keys: (keyof T)[];
    boundKeys?: (keyof T)[];
    dateKey: keyof T;
    stack?: boolean;
    zero?: boolean;
    theme: Partial<ThemeConfig>;
    height?: number;
    width?: number;
    threshold?: 'change' | 'start';
}) => {
    const [disabled, setDisabled] = useState<(keyof T)[]>([]);
    const onKeyToggle = useCallback(
        (key: keyof T) =>
            setDisabled(prev => {
                const next = prev.includes(key) ? prev.filter(st => st !== key) : prev.concat(key);
                if (keys.length - next.length === 0) return prev;
                return next;
            }),
        [setDisabled],
    );
    const isDisabled = useCallback((key: keyof T) => disabled.includes(key), [disabled]);

    const parsedData = useMemo(
        () =>
            data
                .map(datum => ({
                    ...datum,
                    [dateKey]: new Date(datum[dateKey] as string),
                }))
                .sort((a, b) => (a[dateKey]! > b[dateKey]! ? 1 : -1)),
        [data],
    );

    const bounds = useMemo(() => {
        const { min, max, sum } = calculateBounds(parsedData, boundKeys ?? keys);
        if (threshold === 'change') {
            const delta = min! * -1 + max!;
            return {
                min,
                max,
                sum,
                baseline: 0,
                offset: formatPercent((max! / delta) * 100),
            };
        }
        if (threshold === 'start' && keys.length === 1) {
            const baseline = parsedData[0]?.[keys[0]!];
            const delta = max - min;
            const baselineDelta = max - (baseline as unknown as number)!;
            return {
                min,
                max,
                sum,
                baseline,
                offset: formatPercent((baselineDelta! / delta) * 100),
            };
        }

        const delta = min! * -1 + max!;
        const avgDelta = (max! + min!) / 2 - min!;
        const positive = (avgDelta / delta) * 100;
        return {
            min,
            max,
            sum,
            baseline: avgDelta + min!,
            offset: formatPercent(positive),
        };
    }, [parsedData, threshold, keys, boundKeys]);

    const theme = useMemo(() => createTheme(themeOverrides), [themeOverrides]);

    const yScale = useMemo(() => {
        const { sum, min, max } = bounds;
        let domain = [min, max];
        if (stack && threshold === 'change') {
            domain = [0, sum];
        } else if (threshold === 'change') {
            domain = Math.abs(min) > Math.abs(max) ? [-Math.abs(min), Math.abs(min)] : [-Math.abs(max), Math.abs(max)];
        }
        return {
            type: 'linear' as const,
            domain,
            padding: 0,
            nice: false,
            zero: threshold === 'change',
        };
    }, [threshold, bounds, keys.join('-'), boundKeys?.join('-'), stack]);

    const xScale = useMemo(() => {
        const domain = parsedData.map(datum => datum[dateKey]);

        return {
            type: 'band' as const,
            domain,
            padding: 0.2,
            nice: true,
        };
    }, [keys.join('-'), parsedData, dateKey]);

    const bindAccessors = useCallback(
        (key: keyof T) => {
            const isVisible = !isDisabled(key);
            const thresholdProps = threshold ? { y0Accessor: (_: T) => bounds.baseline } : {};

            return {
                ...thresholdProps,
                data: parsedData,
                yAccessor: (d: T) => (isVisible ? d[key] : undefined),
                xAccessor: (d: T) => (isVisible ? d[dateKey] : undefined),
            };
        },
        [isDisabled, threshold],
    );

    return {
        bounds,
        bindAccessors,
        isDisabled,
        onKeyToggle,
        theme,
        xScale,
        yScale,
    };
};
